<!--
  EmailFormCard — the inline email + password mini-form for the email signup path.
  Owns its local field state + validation (email format + password ≥ 8 chars); the
  CTA is gated until valid. Emits `submit({ email, password })`. `disabled` (driven
  by the orchestrator's in-flight guard) blocks double submits. `errorMessage` shows a
  server-side error (e.g. the address is already registered) with a login link; the CTA
  stays disabled until the user edits the email, and `alreadyHaveAccount` routes to login.
-->
<template>
  <div class="email-form-mini-card">
    <div class="signup-field">
      <label class="signup-field-lbl" for="email-mini">Email</label>
      <input
        id="email-mini"
        v-model="email"
        type="email"
        class="signup-field-input"
        placeholder="prenom@email.com"
        autocomplete="email"
      />
    </div>
    <div class="signup-field">
      <label class="signup-field-lbl" for="pwd-mini">Mot de passe</label>
      <div class="signup-field-pwd-wrap">
        <input
          id="pwd-mini"
          v-model="password"
          :type="showPwd ? 'text' : 'password'"
          class="signup-field-input"
          placeholder="8 caractères minimum"
          autocomplete="new-password"
          aria-describedby="pwd-mini-hint"
        />
        <button
          type="button"
          class="signup-field-pwd-toggle"
          :aria-label="showPwd ? 'Masquer le mot de passe' : 'Afficher le mot de passe'"
          @click="showPwd = !showPwd"
        >
          {{ showPwd ? '🙈' : '👁️' }}
        </button>
      </div>
      <!-- Always-visible length rule so a too-short password never looks like a stuck CTA. -->
      <p id="pwd-mini-hint" class="signup-field-hint" :class="{ 'is-ok': pwdValid }" aria-live="polite">
        <span aria-hidden="true">{{ pwdValid ? '✓ ' : '' }}</span>8 caractères minimum
      </p>
    </div>
    <p v-if="showError" class="email-form-error" role="alert">
      <span aria-hidden="true">⚠️ </span>{{ errorMessage
      }}<button v-if="showLoginLink" type="button" class="email-form-login-link" @click="$emit('alreadyHaveAccount')">
        Se connecter
      </button>
    </p>
    <button type="button" class="cta cta-blue" :disabled="!canSubmit || disabled || showError" @click="onSubmit">
      Créer mon compte
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { EMAIL_ALREADY_EXISTS } from '../../config/wordings'

const props = withDefaults(defineProps<{ disabled?: boolean; errorMessage?: string | null }>(), {
  disabled: false,
  errorMessage: null,
})
const emit = defineEmits<{
  submit: [credentials: { email: string; password: string }]
  alreadyHaveAccount: []
}>()

const email = ref('')
const password = ref('')
const showPwd = ref(false)

// The server error is hidden as soon as the user starts fixing the email, and re-shown
// whenever the orchestrator sets a fresh errorMessage (which it first clears to null, so an
// identical message still toggles the prop). This keeps the CTA gated only while an
// un-addressed error is on screen.
const dismissed = ref(false)
watch(
  () => props.errorMessage,
  (msg) => {
    if (msg) dismissed.value = false
  },
)
watch(email, () => {
  dismissed.value = true
})
const showError = computed(() => !!props.errorMessage && !dismissed.value)
// The "Se connecter" link only makes sense for the account-already-exists error — a generic
// validation error (invalid email, …) surfaced from a failed createAccount has no login to offer.
const showLoginLink = computed(() => props.errorMessage === EMAIL_ALREADY_EXISTS)

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const PASSWORD_MIN_LENGTH = 8
const pwdValid = computed(() => password.value.length >= PASSWORD_MIN_LENGTH)
const canSubmit = computed(() => EMAIL_REGEX.test(email.value.trim()) && pwdValid.value)

function onSubmit(): void {
  if (!canSubmit.value) return
  emit('submit', { email: email.value.trim(), password: password.value })
}
</script>

<style scoped>
.email-form-mini-card {
  width: 92%;
  margin: 0 auto;
  background: #f8fbfd; /* off-palette — DS pass to arbitrate */
  border: 1px solid #e2edf5;
  border-radius: 14px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  animation: fadeUp 0.3s both;
}
.signup-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.signup-field-lbl {
  font-size: 11px;
  font-weight: 700;
  color: var(--grey-500);
  text-transform: uppercase;
  letter-spacing: 1.2px;
}
.signup-field-input {
  width: 100%;
  padding: 11px 12px;
  background: var(--white);
  border: 1.5px solid #e2edf5;
  border-radius: 10px;
  font-size: 14px;
  font-family: inherit;
  color: var(--navy-800);
  outline: none;
  transition: border-color 0.15s;
}
.signup-field-input:focus {
  border-color: var(--brand-default);
}
.signup-field-input::placeholder {
  color: #9bbdd4;
}
.signup-field-hint {
  margin: 2px 0 0;
  font-size: 11.5px;
  line-height: 1.3;
  color: var(--grey-500);
}
.signup-field-hint.is-ok {
  color: #16a34a; /* off-palette success green — DS pass to arbitrate */
  font-weight: 600;
}
.signup-field-pwd-wrap {
  position: relative;
}
.signup-field-pwd-wrap .signup-field-input {
  padding-right: 40px;
}
.signup-field-pwd-toggle {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  padding: 4px;
  line-height: 1;
}
.email-form-error {
  margin: 0;
  font-size: 12.5px;
  line-height: 1.4;
  color: #dc2626; /* off-palette error red — DS pass to arbitrate */
}
.email-form-login-link {
  margin-left: 4px;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  font-size: 12.5px;
  font-weight: 700;
  color: var(--blue-800);
  text-decoration: underline;
  cursor: pointer;
}
.cta {
  width: 100%;
  padding: 14px 16px;
  border: none;
  border-radius: 12px;
  background: var(--brand-default);
  color: var(--white);
  font-size: 14px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  line-height: 14px;
  transition:
    background 0.15s,
    opacity 0.15s;
}
.cta:hover:not(:disabled) {
  background: var(--blue-800);
}
.cta:disabled {
  background: #d1d9e0; /* off-palette disabled grey — DS pass to arbitrate */
  color: var(--grey-500);
  cursor: not-allowed;
}
@keyframes fadeUp {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@media (prefers-reduced-motion: reduce) {
  .email-form-mini-card {
    animation: none;
  }
}
</style>
